import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import Button from 'components/ui/Button';
import LoadingMark from 'components/ui/LoadingMark';
import { PAGE_CONTENT_CLASS } from 'components/layout/PageContent';
import { TemplateEditorProvider } from '../create-template/context/TemplateEditorContext';
import TemplateEditorLayout from '../create-template/components/TemplateEditorLayout';
import { hydrateTemplateEditorState } from '../create-template/utils/templateHydration';
import { getUserTemplateById } from '../../services/templateService';

const LoadingState = () => (
  <div className="flex justify-center py-20">
    <LoadingMark message="Carregando modelo..." />
  </div>
);

function formatLoadError(error) {
  if (error == null) return 'Não foi possível carregar o modelo.';
  if (typeof error === 'string') return error;
  if (typeof error === 'object') {
    return error.message || error.error || error.detail || 'Não foi possível carregar o modelo.';
  }
  return 'Não foi possível carregar o modelo.';
}

const EditTemplatePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const templateIdFromUrl = searchParams.get('template_id');

  const [loading, setLoading] = useState(Boolean(templateIdFromUrl));
  const [bootstrap, setBootstrap] = useState(null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (!templateIdFromUrl) {
      toast.error('Modelo não informado.');
      navigate('/user-templates', { replace: true });
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setBootstrap(null);

    (async () => {
      const result = await getUserTemplateById(templateIdFromUrl);
      if (cancelled) return;

      if (!result.success) {
        setLoadError(formatLoadError(result.error));
        setLoading(false);
        return;
      }

      const hydrated = hydrateTemplateEditorState(result.data);
      if (!hydrated.id) {
        setLoadError('Modelo inválido retornado pelo servidor.');
        setLoading(false);
        return;
      }

      setBootstrap(hydrated);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [templateIdFromUrl, navigate]);

  if (!templateIdFromUrl) {
    return null;
  }

  return (
    <>
      {loading ? (
        <div className={PAGE_CONTENT_CLASS}>
          <LoadingState />
        </div>
      ) : loadError ? (
        <div className={`${PAGE_CONTENT_CLASS} flex flex-col items-center justify-center py-20 gap-4`}>
          <p className="text-sm text-destructive text-center max-w-md">{loadError}</p>
          <Button type="button" variant="outline" onClick={() => navigate('/user-templates')}>
            Voltar para Meus Modelos
          </Button>
        </div>
      ) : bootstrap ? (
        <TemplateEditorProvider
          key={bootstrap.id}
          templateId={bootstrap.id}
          initialName={bootstrap.name}
          initialFields={bootstrap.fields}
          initialContent={bootstrap.content}
          editorMountKey={`edit-${bootstrap.id}`}
          isEditMode
        >
          <TemplateEditorLayout
            pageTitle="Editar Modelo | neo-modelos"
            title="Editar Modelo"
            subtitle="Altere campos e conteúdo do seu modelo."
          />
        </TemplateEditorProvider>
      ) : null}
    </>
  );
};

export default EditTemplatePage;
